import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import moment from 'moment';

import { DATE_RANGE_OPTIONS_STATS } from 'config/base';
import { Airport } from 'models/Airport';
import { User } from 'models/User';
import { RootState } from 'store';
import { getRangeStats, RangeParamStats } from 'utils/dates';
import { apiGet, cleanCache } from 'utils/request';

export const fetchAirports = createAsyncThunk('stats/fetchAirports', async () => {
  const data = await apiGet<[string[], Airport[]]>(
    '/static/airports?jmp=[keys(@),values(@)|@[*].{x:longitude,y:latitude}]',
    { action: 'getAirports' },
  );

  const airports = data[0].reduce<Record<string, Airport>>((acc, cur, i) => {
    acc[cur] = data[1][i];
    return acc;
  }, {});

  return airports;
});

type Range = {
  start: string;
  end: string;
  rawStart: Dayjs;
  rawEnd: Dayjs;
};

type HistoryParams = {
  amount: number;
  unit: ManipulateType;
};

export type StatsRange = {
  selected: keyof typeof DATE_RANGE_OPTIONS_STATS;
  current: Range;
  history: Range;
};

interface State {
  range: StatsRange;
  historyParams: HistoryParams;
  refreshTime: number;
  showHistory: boolean;
  isAirportsLoading: boolean;
  airportsError: SerializedError | null;
  airports: Record<string, Airport>;
}

export function getDefaultRange() {
  const params = new URLSearchParams(window.location.search);
  const fromValue = params.get('from');
  const toValue = params.get('to');

  let defaultRange: StatsRange = {
    selected: 'last7d',
    ...getRangeStats('last7d'),
  };

  if (fromValue && toValue) {
    const newRangeStats = {
      start: dayjs(fromValue).utc(),
      end: dayjs(toValue).utc(),
    };

    const newRange = getRangeStats('custom', newRangeStats);
    defaultRange = { selected: 'custom', ...newRange };

    return defaultRange;
  }

  const auth = sessionStorage.getItem('auth');
  if (!auth) {
    return defaultRange;
  }
  const json: { token: string; user: User } = JSON.parse(auth);

  const { user } = json;

  if (!user) {
    return defaultRange;
  }

  const createdAt = user.createdAt;

  if (moment().diff(createdAt, 'days') === 0) {
    defaultRange = {
      selected: 'today',
      ...getRangeStats('today'),
    };
  }

  return defaultRange;
}

const initialState: State = {
  range: getDefaultRange(),
  historyParams: {
    amount: 7,
    unit: 'days',
  },
  refreshTime: 0,
  showHistory: true,
  isAirportsLoading: false,
  airportsError: null,
  airports: {},
};

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    setDateRangeStats(
      state,
      action: PayloadAction<{
        selected: keyof typeof DATE_RANGE_OPTIONS_STATS;
        range?: RangeParamStats;
      }>,
    ) {
      const { range, selected } = action.payload;
      const { amount, unit } = state.historyParams;

      const newRange = getRangeStats(selected, range, amount, unit);

      state.range = {
        selected,
        ...newRange,
      };
    },

    setHistoryParamsStats(
      state,
      action: PayloadAction<{
        amount: number;
        unit: ManipulateType;
      }>,
    ) {
      const { amount, unit } = action.payload;
      const {
        selected,
        current: { rawStart, rawEnd },
      } = state.range;

      const newRange = getRangeStats(
        selected,
        { start: rawStart, end: rawEnd },
        amount,
        unit,
      );

      state.range = {
        selected,
        ...newRange,
      };

      state.historyParams = {
        amount,
        unit,
      };
    },

    refreshStats(state) {
      cleanCache(true);
      const newRange = getRangeStats(
        state.range.selected,
        state.range.selected === 'custom'
          ? { start: state.range.current.rawStart, end: state.range.current.rawEnd }
          : undefined,
        state.historyParams.amount,
        state.historyParams.unit,
      );

      state.range = {
        selected: state.range.selected,
        ...newRange,
      };
      state.refreshTime = Date.now();
    },

    toggleShowHistory(state) {
      state.showHistory = !state.showHistory;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchAirports.pending, (state, action) => {
      state.isAirportsLoading = true;
    });
    builder.addCase(fetchAirports.fulfilled, (state, action) => {
      state.isAirportsLoading = false;
      state.airportsError = null;
      state.airports = action.payload;
    });
    builder.addCase(fetchAirports.rejected, (state, action) => {
      state.isAirportsLoading = false;
      state.airportsError = action.error;
    });
  },
});

export const selectRangeStats = (state: RootState) => state.stats.range;
export const selectRefreshTime = (state: RootState) => state.stats.refreshTime;
export const selectHistoryAmount = (state: RootState) => state.stats.historyParams.amount;
export const selectHistoryUnit = (state: RootState) => state.stats.historyParams.unit;
export const selectAirports = (state: RootState) => state.stats.airports;
export const selectShowHistory = (state: RootState) => state.stats.showHistory;
export const {
  setDateRangeStats,
  setHistoryParamsStats,
  refreshStats,
  toggleShowHistory,
} = statsSlice.actions;
export const reducer = statsSlice.reducer;
